import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function Grid_Intro() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🟦 CSS Grid Layout: Introduction</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS Grid is a two-dimensional layout system for the web. It allows you
        to create complex layouts easily by defining rows, columns, and areas
        for your content. Unlike Flexbox, which is mainly one-dimensional, Grid
        can handle both rows and columns simultaneously.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#1e3a8a" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>display: grid</code> makes a container a grid container.
          </li>
          <li>
            <code>grid-template-columns</code> defines the number and size of
            columns.
          </li>
          <li>
            <code>grid-template-rows</code> defines the number and size of rows.
          </li>
          <li>
            <code>grid-gap</code> (or <code>gap</code>) sets spacing between
            rows and columns.
          </li>
          <li>
            Items can span multiple rows or columns using{" "}
            <code>grid-column</code> and <code>grid-row</code>.
          </li>
        </ul>
      </div>

      {/* Example 1: Basic Grid */}
      <h2 className="section-title">1️⃣ Basic 3x2 Grid</h2>
      <CodeCopyBlock
        code={`.grid-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: 100px 100px;
  gap: 10px;
}
.grid-item {
  background: #3b82f6;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gridTemplateRows: "100px 100px",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            background: "#3b82f6",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          1
        </div>
        <div
          style={{
            background: "#2563eb",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          2
        </div>
        <div
          style={{
            background: "#60a5fa",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          3
        </div>
        <div
          style={{
            background: "#1e40af",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          4
        </div>
        <div
          style={{
            background: "#3b82f6",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          5
        </div>
        <div
          style={{
            background: "#2563eb",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          6
        </div>
      </div>

      {/* Example 2: Grid Item Span */}
      <h2 className="section-title">2️⃣ Grid Item Span</h2>
      <CodeCopyBlock
        code={`.grid-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}
.grid-item {
  background: #10b981;
  color: white;
  padding: 20px;
  border-radius: 8px;
}
.item-large {
  grid-column: span 2;
  grid-row: span 2;
}`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            background: "#10b981",
            color: "white",
            padding: "20px",
            borderRadius: "8px",
          }}
        >
          1
        </div>
        <div
          style={{
            background: "#059669",
            color: "white",
            padding: "20px",
            borderRadius: "8px",
            gridColumn: "span 2",
            gridRow: "span 2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          2 (Large)
        </div>
        <div
          style={{
            background: "#047857",
            color: "white",
            padding: "20px",
            borderRadius: "8px",
          }}
        >
          3
        </div>
        <div
          style={{
            background: "#065f46",
            color: "white",
            padding: "20px",
            borderRadius: "8px",
          }}
        >
          4
        </div>
        <div
          style={{
            background: "#10b981",
            color: "white",
            padding: "20px",
            borderRadius: "8px",
          }}
        >
          5
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fcd34d)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Try <code>grid-template-areas</code> to name layout sections.
          </li>
          <li>
            Combine <code>fr</code>, <code>px</code>, and <code>%</code> for
            flexible sizing.
          </li>
          <li>
            Create responsive layouts using <code>auto-fit</code> and{" "}
            <code>minmax()</code>.
          </li>
          <li>Mix Grid with Flexbox for advanced UI layouts.</li>
          <li>
            Practice spanning multiple rows and columns for complex grids.
          </li>
        </ul>
      </div>
    </div>
  );
}
